import React, {FC, useEffect, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {MainLayoutWithoutStretch, MainContent, RightSidebar} from '../../../Layout/Layout'
import {Button, ButtonRowGrid, ButtonRowWrap} from '../../../Atoms/Buttons'
import TypeBackHeaderTitle, {MenuRow} from '../../../Molecules/ViewComponents/ViewHeader'
import {AlarmSetting} from '../../../../state/rest'
import {BigLabelInput} from '../../../Atoms/Forms'
import {InvisibleContainer, Grid} from '../../../Layout/Grid'
import {useAlarmRuleForm} from './AlarmSettingsUtils'
import LocationsList from '../../../Organisms/Locations/LocationsList'
import DevicesList from '../../../Organisms/Devices/DevicesList'
import AlarmMethodWidget from './AlarmMethodWidget'
import AlarmRecipientsWidget from './AlarmRecipientsWidget'
import WeekScheduleList from '../../../Organisms/Scheduler/WeekScheduleList'
import {Helmet} from 'react-navi-helmet-async'
import AlarmNotificationDelay from './AlarmNotificationDelay'
import ConfirmModal from '../../../Molecules/Modals/ConfirmModal'

interface FormProps {
  defaultValues?: AlarmSetting
}
const EditAlarmRule: FC<FormProps> = ({defaultValues}: FormProps): any => {
  const {t} = useTranslation('alarms')

  const saved = defaultValues ? defaultValues : undefined

  const fc = useAlarmRuleForm(saved)
  const {register, isValid, errors, onDelete, onCancel, devices, loading, onSave, backPath, sc, amc, uc, rc} = fc
  const isEditing = !!saved
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)

  const confirmDelete = () => {
    setDeleteModalOpen(false)
    onDelete()
  }

  return (
    <MainLayoutWithoutStretch>
      <ConfirmModal
        heading={t('alarms:confirmation.deleteAlarmRuleTitle', 'Delete alarm message')}
        content={t(
          'alarms:confirmation.deleteAlarmRuleDescription',
          'Are you sure you want to delete this alarm message?'
        )}
        actions={[
          {label: t('common:actions.cancel', 'Cancel'), action: () => setDeleteModalOpen(false)},
          {label: t('common:actions.delete', 'Delete'), action: confirmDelete}
        ]}
        isOpen={deleteModalOpen}
        closeModal={() => setDeleteModalOpen(false)}
      />
      {isEditing ? (
        <Helmet title={t('common:routes.editAlarmRule', 'Edit alarm rule')} />
      ) : (
        <Helmet title={t('common:routes.createAlarmRule', 'Create alarm rule')} />
      )}
      <MainContent variant="white">
        <MenuRow showMenu={isEditing} onDelete={() => setDeleteModalOpen(true)}>
          <TypeBackHeaderTitle
            label={t('common:backHeaderTitles.alarmRule', 'Alarm rule')}
            path={backPath}
            backLabel={t('common:routes.alarmMessageSettings', 'Alarm message settings')}
            isEditing={isEditing}
          />
        </MenuRow>
        <InvisibleContainer>
          <MainForm register={register} errors={errors} sc={sc} amc={amc} uc={uc} fc={fc} devices={devices} t={t} />
          <ButtonRowWrap>
            <div className="empty"></div>
            <ButtonRowGrid>
              <Button name="cancel-button" negative variant="secondary" disabled={loading} onClick={onCancel}>
                {t('common:buttons.cancel', 'Cancel')}
              </Button>
              <Button
                variant="secondary"
                disabled={
                  !isValid ||
                  loading ||
                  rc.list.length === 0 ||
                  sc.schedules.length === 0 ||
                  amc.selected.length === 0 ||
                  !sc.isValid ||
                  devices.getSelectedDevices().length === 0
                }
                onClick={onSave}
              >
                {t('common:buttons.save', 'Save')}
              </Button>
            </ButtonRowGrid>
          </ButtonRowWrap>
        </InvisibleContainer>
      </MainContent>
      <RightSidebar>
        <LocationsList
          controller={rc}
          cyKey={'alarm-rule'}
          label={t('alarms:labels.selectAlarmTargets', 'Select which sites alarms are sent')}
        />
      </RightSidebar>
    </MainLayoutWithoutStretch>
  )
}

interface MainFormProps {
  register: any
  errors: any
  sc: any
  amc: any
  uc: any
  fc: any
  devices: any
  t: any
}

const MainForm = ({register, errors, sc, amc, uc, fc, devices, t}: MainFormProps) => {
  return (
    <Grid>
      <div>
        <BigLabelInput
          required
          placeholder={t('alarms:placeholders.newAlarmRule', 'New alarm rule name')}
          labelText={t('alarms:labels.newAlarmRule', 'Alarm rule name')}
          name="name"
          id="name"
          ref={register({required: true})}
          errorMsg={!!errors.name ? t('common:validation.nameRequired', 'Name field is required') : undefined}
        />
        <WeekScheduleList controller={sc} />
      </div>
      <div>
        <AlarmRecipientsWidget controller={uc} />
        <AlarmMethodWidget controller={amc} />
        <AlarmNotificationDelay controller={fc} />
        <DevicesList
          controller={devices}
          cyKey={'alarm-rule-devices'}
          label={t('alarms:labels.selectDeviceTargets', 'Select which device alarms are sent')}
        />
      </div>
    </Grid>
  )
}

export default EditAlarmRule
